// @ts-nocheck -- TypeScript migration in progress: bulk-renamed from .js and not
// yet typed. Remove this directive and add real types when converting this
// module. Backlog: grep -rl "@ts-nocheck" src. See docs/typescript-migration.md.
// Chart series toggles + phase filter dropdowns:
// - setupSeriesControls: build the temperature + secondary series
//   checkbox groups for both the flight and explorer panels, wire
//   alt-click/dblclick isolate, subgroup master toggles, active
//   series updates, and the channel→pane move/drag/reset controls.
// - renderSeriesChipStrips: always-visible strip of active-channel
//   chips in each drawer summary (EGT/CHT cluster into one chip per
//   bank); chips toggle on click, isolate on double-click, and drag
//   onto a graph pane like legend rows. A chip toggled off stays in
//   the strip (dimmed) until the next rebuild so it can be re-enabled.
// - isolateSeries: clear active set and select a single series.
// - seriesControlGroupHtml / temperatureSeriesControlGroupHtml /
//   seriesControlSubgroupHtml / seriesControlHtml: markup builders.
// - engineTemperatureSeriesLabel / seriesUnitSuffix /
//   singleEngineIndexedSeriesLabel / engineAwareSeriesLabel:
//   per-engine label resolution for multi-engine aircraft and
//   disambiguation for indexed single-engine side channels.
// - availablePhases / phaseOptionLabel / phaseSampleCounts /
//   updatePhaseFilters: phase select dropdowns and label formatting.
// - syncSeriesControls: keep checkboxes + subgroup masters in sync
//   with the activeSeries set.

import { setupFloatingSeriesPanels } from "./series-floating-panel.js";

import {
  enforceUnalignedSeriesExclusivity,
} from "./series-alignment.js";

export function createSeriesControls({
  escapeHtml,
  seriesMeta,
  titleCase,
  scheduleDrawAll,
  syncChartRouteState = () => {},
  // Time-unaligned CGR combos: 30C-sourced channel keys for the loaded flight
  // (empty when aligned) + a notifier for the auto-swap toast. Optional so unit
  // harnesses and non-combo flights are unaffected.
  unalignedAuxSeriesKeys = () => new Set(),
  notifySeriesExclusion = () => {},
  currentEngineDescriptors,
  engineScopedChannelLabel,
  availableSeriesKeys,
  isPrimaryTemperatureSeries,
  isPrimaryPaneSeries,
  moveSeriesToPane,
  hasSeriesPaneOverrides,
  resetSeriesPaneOverrides,
  bindSeriesPaneDrag,
  availablePhasesForData,
  currentFlightDerivedCache,
  sampleIntervalSeconds,
  formatElapsedTime,
  activeSeries,
  getFlightData,
}) {
  // Enforce one-group-at-a-time on unaligned combos after any activeSeries
  // mutation. `changedKeys` are the just-enabled keys (their group wins).
  function applySeriesAlignmentExclusion(changedKeys) {
    const { removedKeys, keptGroup } = enforceUnalignedSeriesExclusivity(
      activeSeries,
      unalignedAuxSeriesKeys(),
      changedKeys,
    );
    if (removedKeys.length && keptGroup) notifySeriesExclusion(keptGroup);
    return removedKeys.length > 0;
  }

  function setupSeriesControls() {
    setupFloatingSeriesPanels();
    const containers = [
      document.querySelector("#seriesControls"),
      document.querySelector("#explorerSeriesControls"),
    ].filter(Boolean);

    containers.forEach((container) => {
      container.classList.add("series-control-groups");
      const keys = availableSeriesKeys();
      const primaryKeys = keys.filter(isPrimaryPaneSeries);
      const secondaryKeys = keys.filter((key) => !isPrimaryPaneSeries(key));
      container.innerHTML = [
        temperatureSeriesControlGroupHtml(primaryKeys),
        secondarySeriesControlGroupHtml(secondaryKeys),
      ].filter(Boolean).join("");

      container.querySelectorAll("button[data-move-series]").forEach((button) => {
        // The move button lives inside the toggle label: keep its clicks from
        // arming a drag, toggling the checkbox, or isolating on double-click.
        button.addEventListener("pointerdown", (event) => event.stopPropagation());
        button.addEventListener("dblclick", (event) => {
          event.preventDefault();
          event.stopPropagation();
        });
        button.addEventListener("click", (event) => {
          event.preventDefault();
          event.stopPropagation();
          moveSeriesToPane(button.dataset.moveSeries, button.dataset.moveTarget);
        });
      });

      // The reset button lives in the drawer's static gesture-hint row, outside
      // this rebuilt container; onclick assignment keeps rebinding idempotent.
      const resetButton = container.closest(".chart-series-drawer")?.querySelector("[data-series-pane-reset]");
      if (resetButton) {
        resetButton.hidden = !hasSeriesPaneOverrides();
        resetButton.onclick = () => {
          resetSeriesPaneOverrides();
          setupSeriesControls();
          scheduleDrawAll();
        };
      }

      bindSeriesPaneDrag(container);

      container.querySelectorAll("input[data-series]").forEach((input) => {
        const label = input.closest("label");
        label?.addEventListener("click", (event) => {
          if (!event.altKey) return;
          event.preventDefault();
          event.stopPropagation();
          isolateSeries(input.dataset.series);
        }, true);
        label?.addEventListener("dblclick", (event) => {
          event.preventDefault();
          event.stopPropagation();
          isolateSeries(input.dataset.series);
        });
        input.addEventListener("keydown", (event) => {
          if (!event.altKey || (event.key !== "Enter" && event.key !== " ")) return;
          event.preventDefault();
          isolateSeries(input.dataset.series);
        });
        input.addEventListener("change", () => {
          input.checked ? activeSeries.add(input.dataset.series) : activeSeries.delete(input.dataset.series);
          if (input.checked) applySeriesAlignmentExclusion([input.dataset.series]);
          renderSeriesChipStrips();
          syncSeriesControls();
          scheduleDrawAll();
          syncChartRouteState();
        });
      });

      container.querySelectorAll("input[data-subgroup-toggle]").forEach((master) => {
        master.addEventListener("change", () => {
          const subgroup = master.closest(".series-control-subgroup");
          const childKeys = [];
          subgroup?.querySelectorAll("input[data-series]").forEach((child) => {
            if (master.checked) {
              activeSeries.add(child.dataset.series);
              childKeys.push(child.dataset.series);
            } else {
              activeSeries.delete(child.dataset.series);
            }
          });
          if (master.checked) applySeriesAlignmentExclusion(childKeys);
          renderSeriesChipStrips();
          syncSeriesControls();
          scheduleDrawAll();
          syncChartRouteState();
        });
      });
    });

    renderSeriesChipStrips();
    syncSeriesControls();
    updatePhaseFilters();
  }

  function chipKeysFromElement(chip) {
    return String(chip.dataset.chipKeys ?? "").split(",").filter(Boolean);
  }

  function toggleSeriesKeys(keys) {
    const anyActive = keys.some((key) => activeSeries.has(key));
    keys.forEach((key) => {
      anyActive ? activeSeries.delete(key) : activeSeries.add(key);
    });
    if (!anyActive) applySeriesAlignmentExclusion(keys);
    syncSeriesControls();
    scheduleDrawAll();
    syncChartRouteState();
  }

  function isolateSeriesKeys(keys) {
    const available = availableSeriesKeys();
    const valid = keys.filter((key) => available.includes(key));
    if (!valid.length) return;
    activeSeries.clear();
    valid.forEach((key) => activeSeries.add(key));
    applySeriesAlignmentExclusion(valid);
    syncSeriesControls();
    requestAnimationFrame(syncSeriesControls);
    scheduleDrawAll();
    syncChartRouteState();
  }

  function chipIndexListLabel(keys) {
    const indices = keys
      .map((key) => Number(String(key).match(/(\d+)$/)?.[1]))
      .filter(Number.isFinite)
      .sort((a, b) => a - b);
    if (indices.length !== keys.length || indices.length < 2) return `(${keys.length})`;
    const contiguous = indices.every((value, i) => i === 0 || value === indices[i - 1] + 1);
    return contiguous ? `${indices[0]}–${indices[indices.length - 1]}` : indices.join(", ");
  }

  function seriesChipUnits() {
    const available = availableSeriesKeys();
    const activeKeys = available.filter((key) => activeSeries.has(key));
    const engines = currentEngineDescriptors();
    const units = [];
    const clustered = new Set();

    if (engines.length > 1) {
      engines.forEach((engine) => {
        [["egtKey", "EGT"], ["chtKey", "CHT"]].forEach(([cylinderProp, base]) => {
          const memberKeys = (engine.cylinders ?? [])
            .map((cylinder) => cylinder[cylinderProp])
            .filter((key) => key && activeSeries.has(key) && available.includes(key));
          if (memberKeys.length < 2) return;
          memberKeys.forEach((key) => clustered.add(key));
          const prefix = engine.shortLabel ? `${engine.shortLabel} ` : "";
          units.push({ keys: memberKeys, label: `${prefix}${base} ${chipIndexListLabel(memberKeys)}` });
        });
      });
    } else {
      ["egt", "cht"].forEach((base) => {
        const memberKeys = activeKeys.filter((key) => new RegExp(`^${base}\\d+$`).test(key));
        if (memberKeys.length < 2) return;
        memberKeys.forEach((key) => clustered.add(key));
        units.push({ keys: memberKeys, label: `${base.toUpperCase()} ${chipIndexListLabel(memberKeys)}` });
      });
    }

    activeKeys
      .filter((key) => !clustered.has(key))
      .forEach((key) => units.push({ keys: [key], label: engineAwareSeriesLabel(key) }));
    return units;
  }

  function seriesChipHtml(unit) {
    const keysAttr = escapeHtml(unit.keys.join(","));
    const color = seriesMeta[unit.keys[0]]?.color ?? "currentColor";
    const label = escapeHtml(unit.label);
    return `
      <label class="series-chip" role="button" tabindex="0" aria-pressed="true" data-series-drag="${keysAttr}" data-chip-keys="${keysAttr}" title="Toggle ${label}. Double-click to isolate. Drag onto a graph to move it there.">
        <svg class="series-chip-grip" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><circle cx="9" cy="5.5" r="1.7"/><circle cx="9" cy="12" r="1.7"/><circle cx="9" cy="18.5" r="1.7"/><circle cx="15" cy="5.5" r="1.7"/><circle cx="15" cy="12" r="1.7"/><circle cx="15" cy="18.5" r="1.7"/></svg>
        <span class="series-chip-dot" style="background:${color}"></span>
        <span class="series-chip-label">${label}</span>
      </label>
    `;
  }

  function seriesChipAddHtml() {
    return `
      <span class="series-chip series-chip-add" aria-hidden="true">
        <svg viewBox="0 0 24 24" focusable="false"><path d="M12 5v14"></path><path d="M5 12h14"></path></svg>
        <span class="series-chip-add-closed">Add traces</span>
        <span class="series-chip-add-open">Hide list</span>
      </span>
    `;
  }

  function renderSeriesChipStrips() {
    const strips = document.querySelectorAll(".series-chip-strip");
    if (!strips.length) return;
    const hasChannels = availableSeriesKeys().length > 0;
    const chipsHtml = hasChannels ? seriesChipUnits().map(seriesChipHtml).join("") + seriesChipAddHtml() : "";
    strips.forEach((strip) => {
      strip.hidden = !hasChannels;
      strip.innerHTML = chipsHtml;
      bindSeriesPaneDrag(strip);
      strip.querySelectorAll("label[data-chip-keys]").forEach((chip) => {
        // Chips live inside the drawer <summary>: preventDefault keeps a chip
        // click from also expanding or collapsing the drawer.
        chip.addEventListener("click", (event) => {
          event.preventDefault();
          event.stopPropagation();
          if (event.altKey) isolateSeriesKeys(chipKeysFromElement(chip));
          else toggleSeriesKeys(chipKeysFromElement(chip));
        });
        chip.addEventListener("dblclick", (event) => {
          event.preventDefault();
          event.stopPropagation();
          isolateSeriesKeys(chipKeysFromElement(chip));
        });
        chip.addEventListener("keydown", (event) => {
          if (event.key !== "Enter" && event.key !== " ") return;
          event.preventDefault();
          event.stopPropagation();
          if (event.altKey) isolateSeriesKeys(chipKeysFromElement(chip));
          else toggleSeriesKeys(chipKeysFromElement(chip));
        });
      });
    });
  }

  function isolateSeries(key) {
    if (!key) return;
    isolateSeriesKeys([key]);
  }

  function seriesControlGroupHtml(label, keys, pane = "primary") {
    if (!keys.length) return "";
    return `
      <div class="series-control-group" data-pane-drop="${pane}">
        <div class="series-control-heading">${label}</div>
        <div class="series-control-items">
          ${keys.map((key) => seriesControlHtml(key)).join("")}
        </div>
      </div>
    `;
  }

  function primaryPaneHeading(keys) {
    return keys.every(isPrimaryTemperatureSeries) ? "Temperature Graph" : "Primary Graph";
  }

  // Picker sections for the secondary-pane channels, keyed by the series base
  // key (engine suffixes 1/2/Left/Right strip to the same base). EGT/CHT keys
  // relocated to the secondary pane get their own Temperatures section.
  const secondarySystemByBaseKey = {
    fuelFlow: "Engine",
    fuelUsed: "Engine",
    fuelPressure: "Engine",
    percentPower: "Engine",
    powerRopHp: "Engine",
    percentPowerRop: "Engine",
    powerLopHp: "Engine",
    percentPowerLop: "Engine",
    rpm: "Engine",
    map: "Engine",
    oilTemp: "Engine",
    coolantTemp: "Engine",
    oilPressure: "Engine",
    tit: "Engine",
    carbTemp: "Engine",
    upperDeckPressure: "Engine",
    inductionTemp: "Engine",
    compressorDischargeTemp: "Engine",
    buss: "Electrical",
    amps: "Electrical",
    oat: "Air & Speeds",
    ias: "Air & Speeds",
    tas: "Air & Speeds",
    groundSpeed: "Air & Speeds",
    altitude: "Flight Path",
    verticalSpeed: "Flight Path",
    pitch: "Flight Path",
    roll: "Flight Path",
  };
  const secondarySystemOrder = ["Engine", "Electrical", "Air & Speeds", "Flight Path", "Temperatures", "Other"];

  function secondarySystemForKey(key) {
    if (/^(egt|cht)\d*$/.test(key)) return "Temperatures";
    const base = String(key).replace(/(?:1|2|Left|Right)$/, "");
    return secondarySystemByBaseKey[key] ?? secondarySystemByBaseKey[base] ?? "Other";
  }

  function secondarySeriesControlGroupHtml(keys) {
    if (!keys.length) return "";
    const buckets = new Map();
    keys.forEach((key) => {
      const system = secondarySystemForKey(key);
      if (!buckets.has(system)) buckets.set(system, []);
      buckets.get(system).push(key);
    });
    const subgroups = secondarySystemOrder
      .filter((system) => buckets.has(system))
      .map((system) => ({ label: system, keys: buckets.get(system) }));
    // A single system gains nothing from a section heading + master toggle;
    // keep the flat list the picker has always shown.
    if (subgroups.length < 2) return seriesControlGroupHtml("Secondary Graph", keys, "secondary");
    return `
      <div class="series-control-group" data-pane-drop="secondary">
        <div class="series-control-heading">Secondary Graph</div>
        <div class="series-control-subgroups series-system-subgroups">
          ${subgroups.map(seriesControlSubgroupHtml).join("")}
        </div>
      </div>
    `;
  }

  function temperatureSeriesControlGroupHtml(keys) {
    if (!keys.length) return "";
    const engines = currentEngineDescriptors();
    if (engines.length <= 1) return seriesControlGroupHtml(primaryPaneHeading(keys), keys);

    const available = new Set(keys);
    const groupedKeys = new Set();
    const subgroups = engines.map((engine) => {
      const engineKeys = [
        ...(engine.cylinders ?? []).map((cylinder) => cylinder.egtKey),
        ...(engine.cylinders ?? []).map((cylinder) => cylinder.chtKey),
      ].filter((key) => key && available.has(key));
      engineKeys.forEach((key) => groupedKeys.add(key));
      return {
        label: engine.label ?? `${engine.shortLabel} Engine`,
        keys: engineKeys,
        labelForKey: (key) => engineTemperatureSeriesLabel(key, engine),
      };
    }).filter((group) => group.keys.length);

    const ungroupedKeys = keys.filter((key) => !groupedKeys.has(key));
    if (ungroupedKeys.length) {
      subgroups.push({
        label: "Aircraft",
        keys: ungroupedKeys,
        labelForKey: engineAwareSeriesLabel,
      });
    }

    if (!subgroups.length) return seriesControlGroupHtml(primaryPaneHeading(keys), keys);
    return `
      <div class="series-control-group" data-pane-drop="primary">
        <div class="series-control-heading">${primaryPaneHeading(keys)}</div>
        <div class="series-control-subgroups">
          ${subgroups.map(seriesControlSubgroupHtml).join("")}
        </div>
      </div>
    `;
  }

  function seriesControlSubgroupHtml(group) {
    const label = escapeHtml(group.label);
    return `
      <div class="series-control-subgroup">
        <label class="series-control-subheading" title="Toggle every ${label} trace">
          <input type="checkbox" data-subgroup-toggle aria-label="Toggle all ${label} traces">
          <span>${label}</span>
        </label>
        <div class="series-control-items">
          ${group.keys.map((key) => seriesControlHtml(key, { labelForKey: group.labelForKey, showRawAlias: true })).join("")}
        </div>
      </div>
    `;
  }

  function engineTemperatureSeriesLabel(key, engine) {
    const scoped = engineScopedChannelLabel(key, currentEngineDescriptors(), { style: "narrative" });
    if (scoped) return scoped;
    return engineAwareSeriesLabel(key);
  }

  function seriesUnitSuffix(label) {
    return String(label ?? "").match(/\s*(\([^)]*\))\s*$/)?.[1] ?? "";
  }

  function averageSeriesLabel(key, rawLabel, engines) {
    if ((engines?.length ?? 0) <= 1) return null;
    if (engineScopedChannelLabel(key, engines, { style: "narrative" })) return null;
    const meta = seriesMeta[key];
    if (!meta?.auxGroup && !["egt", "cht"].includes(key)) return null;
    return `Avg ${String(rawLabel ?? titleCase(key)).replace(/^Engine\s+/i, "")}`;
  }

  function singleEngineIndexedSeriesLabel(key, rawLabel) {
    const text = String(rawLabel ?? titleCase(key));
    const match = String(key ?? "").match(/^(fuelFlow|fuelUsed|rpm|map|oilTemp|oilPressure|amps|tit)(1|2|Left|Right)$/);
    if (!match) return null;
    const suffix = seriesUnitSuffix(text);
    const base = suffix ? text.slice(0, -suffix.length).trim() : text;
    const channel = { "1": "1", "2": "2", Left: "Left", Right: "Right" }[match[2]];
    return suffix ? `${base} ${channel} ${suffix}` : `${base} ${channel}`;
  }

  function engineAwareSeriesLabel(key) {
    const rawLabel = seriesMeta[key]?.label ?? titleCase(key);
    const engines = currentEngineDescriptors();
    if (engines.length <= 1) return singleEngineIndexedSeriesLabel(key, rawLabel) ?? rawLabel;
    const scoped = engineScopedChannelLabel(key, engines, { style: "narrative" });
    if (scoped) {
      const suffix = seriesUnitSuffix(rawLabel);
      return suffix && !scoped.endsWith(suffix) ? `${scoped} ${suffix}` : scoped;
    }
    const averageLabel = averageSeriesLabel(key, rawLabel, engines);
    if (averageLabel) return averageLabel;
    return rawLabel;
  }

  function seriesPaneMoveButtonHtml(key, label) {
    const target = isPrimaryPaneSeries(key) ? "secondary" : "primary";
    const targetLabel = target === "primary" ? "primary (temperature) graph" : "secondary graph";
    const arrow = target === "primary" ? "&#8593;" : "&#8595;";
    return `<button type="button" class="series-pane-move" data-move-series="${key}" data-move-target="${target}" title="Move to the ${targetLabel}" aria-label="Move ${label} to the ${targetLabel}">${arrow}</button>`;
  }

  function seriesControlHtml(key, options = {}) {
    const meta = seriesMeta[key];
    const rawLabel = meta.label;
    const displayLabel = options.labelForKey?.(key) ?? engineAwareSeriesLabel(key);
    const hasAlias = options.showRawAlias && rawLabel && displayLabel && rawLabel !== displayLabel;
    const label = escapeHtml(hasAlias ? `${displayLabel} (${rawLabel})` : displayLabel);
    const titleLabel = escapeHtml(hasAlias ? `${displayLabel}, logged as ${rawLabel}` : displayLabel);
    const unalignedHint = unalignedAuxSeriesKeys().has(key)
      ? " 30C channel: its clock is not aligned with the 30P on this flight, so showing it hides the 30P traces."
      : "";
    return `
      <label data-series-drag="${key}" title="Toggle ${titleLabel}. Option-click or double-click to isolate this trace. Drag onto a graph to move it there.${unalignedHint}">
        <input type="checkbox" data-series="${key}" aria-label="${label}" ${activeSeries.has(key) ? "checked" : ""}>
        <span style="color:${meta.color}">${label}</span>
        ${seriesPaneMoveButtonHtml(key, label)}
      </label>
    `;
  }

  function availablePhases(data = getFlightData()) {
    return availablePhasesForData(data);
  }

  function phaseOptionLabel(phase) {
    const flightData = getFlightData();
    const count = phaseSampleCounts().get(phase) ?? 0;
    const seconds = count * sampleIntervalSeconds(flightData);
    return `${phase} (${formatElapsedTime(seconds)})`;
  }

  function phaseSampleCounts() {
    const cache = currentFlightDerivedCache();
    if (cache.phaseCounts) return cache.phaseCounts;
    const counts = new Map();
    getFlightData().forEach((point) => {
      if (!point.phase) return;
      counts.set(point.phase, (counts.get(point.phase) ?? 0) + 1);
    });
    cache.phaseCounts = counts;
    return counts;
  }

  function updatePhaseFilters() {
    const phases = availablePhases();
    ["#phaseFilter", "#explorerPhaseFilter"].forEach((selector) => {
      const select = document.querySelector(selector);
      if (!select) return;
      const previous = select.value || "all";
      select.innerHTML = `
        <option value="all">Full flight</option>
        ${phases.map((phase) => `<option value="${escapeHtml(phase)}">${escapeHtml(phaseOptionLabel(phase))}</option>`).join("")}
      `;
      select.value = phases.includes(previous) ? previous : "all";
    });
  }

  function syncSeriesControls() {
    document.querySelectorAll("input[data-series]").forEach((input) => {
      input.checked = activeSeries.has(input.dataset.series);
    });
    document.querySelectorAll("input[data-subgroup-toggle]").forEach((master) => {
      const subgroup = master.closest(".series-control-subgroup");
      const children = subgroup ? Array.from(subgroup.querySelectorAll("input[data-series]")) : [];
      if (!children.length) {
        master.checked = false;
        master.indeterminate = false;
        return;
      }
      const activeCount = children.filter((child) => activeSeries.has(child.dataset.series)).length;
      master.checked = activeCount === children.length;
      master.indeterminate = activeCount > 0 && activeCount < children.length;
    });
    const availableKeys = availableSeriesKeys();
    // Flight loads and pane-split defaults flip activeSeries outside the
    // picker handlers; rebuild the chip strips whenever an active channel has
    // no chip yet so those paths surface new chips too. A chip toggled off
    // stays (dimmed) — only a missing active channel forces a rebuild.
    if (document.querySelector(".series-chip-strip")) {
      const represented = new Set();
      document.querySelectorAll(".series-chip[data-chip-keys]").forEach((chip) => {
        chipKeysFromElement(chip).forEach((key) => represented.add(key));
      });
      if (availableKeys.some((key) => activeSeries.has(key) && !represented.has(key))) {
        renderSeriesChipStrips();
      }
    }
    document.querySelectorAll(".series-chip[data-chip-keys]").forEach((chip) => {
      const keys = chipKeysFromElement(chip);
      const activeCount = keys.filter((key) => activeSeries.has(key)).length;
      chip.classList.toggle("is-off", activeCount === 0);
      chip.classList.toggle("is-partial", activeCount > 0 && activeCount < keys.length);
      chip.setAttribute("aria-pressed", activeCount === keys.length ? "true" : activeCount ? "mixed" : "false");
    });
    const shownCount = availableKeys.filter((key) => activeSeries.has(key)).length;
    document.querySelectorAll("[data-series-drawer-count]").forEach((count) => {
      count.textContent = availableKeys.length
        ? `${shownCount} of ${availableKeys.length} channels shown`
        : "Show or hide graph channels";
    });
  }

  return {
    setupSeriesControls,
    renderSeriesChipStrips,
    seriesChipUnits,
    isolateSeries,
    seriesControlGroupHtml,
    secondarySeriesControlGroupHtml,
    secondarySystemForKey,
    temperatureSeriesControlGroupHtml,
    seriesControlSubgroupHtml,
    engineTemperatureSeriesLabel,
    seriesUnitSuffix,
    singleEngineIndexedSeriesLabel,
    engineAwareSeriesLabel,
    seriesControlHtml,
    availablePhases,
    phaseOptionLabel,
    phaseSampleCounts,
    updatePhaseFilters,
    syncSeriesControls,
  };
}
